import { useCallback, useContext, useState } from "react";
import { useSwitchOrganizationMutation } from "@/application/queries/organizations";
import { useSessionCache } from "@/application/queries";
import { ROUTES } from "@/app/routes";
import { AuthContext } from "@/contexts/AuthContext";
import { useOrganizationStore } from "@/stores/useOrganizationStore";
import { useToastStore } from "@/stores/useToastStore";
import { resetAllFilterStores } from "@/stores/useResetAllFilterStore";

const RECORD_ROUTE_PREFIXES = [
  ROUTES.BENEFICIARIES.DETAIL(""),
  ROUTES.INVESTORS.DETAIL(""),
  ROUTES.PROJECTS.DETAIL(""),
  ROUTES.INVESTMENTS.DETAIL(""),
  ROUTES.INVESTMENTS.CONTRACT_DETAIL(""),
  ROUTES.ORGANIZATION.TEMPLATE_DETAIL(""),
];

const isRecordRoute = (pathname: string) =>
  RECORD_ROUTE_PREFIXES.some(
    (prefix) => pathname.startsWith(prefix) && pathname.length > prefix.length,
  );

export const useSwitchOrganization = (pathname: string, search: string) => {
  const session = useSessionCache();
  const checkAuthStatus = useContext(AuthContext)?.checkAuthStatus;
  const { mutateAsync } = useSwitchOrganizationMutation();
  const isSwitching = useOrganizationStore((state) => state.isSwitching);
  const setSwitching = useOrganizationStore((state) => state.setSwitching);
  const [destination, setDestination] = useState<string | null>(null);

  const switchOrganization = useCallback(
    async (organizationId: string | number) => {
      setSwitching(true);
      try {
        await session.halt();
        const result = await mutateAsync(organizationId);
        session.dropOrganizationData();
        resetAllFilterStores();
        useOrganizationStore.getState().setSelectedOrganizationUuid(result.organizationId);
        useToastStore.getState().clearAll();
        const next = isRecordRoute(pathname) ? ROUTES.DASHBOARD : search ? pathname : null;
        setDestination(next);
        await checkAuthStatus?.();
        return { ...result, destination: next };
      } catch (error) {
        setDestination(null);
        throw error;
      } finally {
        setSwitching(false);
      }
    },
    [checkAuthStatus, mutateAsync, pathname, search, session, setSwitching],
  );

  return { switchOrganization, isSwitching, destination };
};
